/**
 * SOLID Review
 * S — Strong. One concern: run a client job now and on an interval while its model wants it.
 * O — Strong. Any job; nothing here names one.
 * L — N/A — no hierarchy.
 * I — Strong. One reaction, which a model composes among its others.
 * D — Strong. The timers it is handed.
 */
import type { IConfigReaction } from './Reactions';
import type { IServiceViewTimers } from './platform/IConfigDepViewPlatform';

/** What a job's model hands it: the job, its interval, and whether it runs. */
type TJobInputs = readonly [run: () => void, intervalMs: number, enabled: boolean];

/**
 * A RECURRING CLIENT JOB (frontend strict-ports plan, FE1e, 2026-10-03) — the client twin of the backend's `BackgroundJobs`: one
 * use-case call per tick, nothing decided here. Runs at once, then every `intervalMs`, while `enabled`, and stops when its view
 * leaves. The lobby's self-heal poll and the video's idle tick are its two jobs.
 *
 * `useClientJob`'s effect until frontend-framework-oo P2 (§28.294), a view model of its own until P4 (§28.296): both its jobs are
 * reactions of the models that run them now — the LiveKit session's idle tick and the lobby's poll — each declared in its place in
 * its model's list.
 */
export class ClientJobModel {
  /** A job as one reaction of a list: `factsOf` reads the job, its interval and whether it runs from the list's facts. */
  static reaction<TFacts>(timers: IServiceViewTimers, factsOf: (facts: TFacts) => TJobInputs): IConfigReaction<TFacts> {
    const reaction: IConfigReaction<TFacts> = {
      deps: (facts) => factsOf(facts),
      run: (facts) => {
        const [run, intervalMs, enabled] = factsOf(facts);
        if (!enabled) return undefined;
        run();
        const timer = timers.setInterval(run, intervalMs);
        return () => timers.clearInterval(timer);
      },
    };
    return reaction;
  }
}
